import { GENIUS_API_URL, HEY_GENIUS_FIELD, WIDGET_BASE } from 'src/constants';
import { DataRetrievalStrategy, Publication, WidgetConfig } from 'src/types';
import { sourcesToHighlightsAndCuepoints } from '@libertify.frontend/adapters';
import { defaultConfig } from './defaultConfig';
import merge from 'deepmerge';
// import highlights, { configs, videos } from './highlights';
const highlights = {};
const configs = {};
const videos = {};

type URLDictionary = {
  config: string;
  asset: string;
  publication: string;
};
export class URLBuilder {
  public static build(
    url: string,
    options: {
      organization_id: string;
      product_sku: string;
      assets?: string[];
      language?: string;
      alt_config_url?: string;
      alt_publication_base_url?: string;
      widget_base?: string;
    },
  ): URLDictionary {
    const theUrl = this.buildUrl(url, options);
    return {
      config: this.config(theUrl, options),
      asset: this.asset(theUrl),
      publication: this.publication(theUrl, options),
    };
  }

  private static buildUrl(
    url,
    _options: Parameters<typeof URLBuilder.build>[1],
  ) {
    // Not of much use for now, but could be useful later
    return url;
  }

  private static asset(baseUrl) {
    return `${baseUrl}`;
  }

  private static config(baseUrl, options) {
    if (options.alt_config_url) {
      return `${options.alt_config_url}`;
    }
    if (options.widget_base) {
      return `${options.widget_base}/data/organizations/${options.organization_id}/${options.app_id}/config.json`;
    }
    return `${baseUrl}/config.json`;
  }
  private static publication(baseUrl, options) {
    let theBaseUrl = baseUrl;
    if (options.alt_publication_base_url) {
      theBaseUrl = options.alt_publication_base_url;
    }
    const result = `${theBaseUrl}/media/organizations/${options.organization_id}/publications/public?language=${options.language ?? 'en-US'}`;
    const publicationIdentifier = options.product_sku
      ? options.product_sku
      : options.page_id;
    const publicationIdentifierKey = options.product_sku
      ? 'product_sku'
      : 'page_id';
    return `${result}&${publicationIdentifierKey}=${publicationIdentifier}`;
  }
}
export class LocalDataRetrievalStrategy implements DataRetrievalStrategy {
  private readonly _pageId = window?.[HEY_GENIUS_FIELD]?.page_id;
  private readonly legacyPageId = window.location.pathname;
  private get pageId() {
    return this._pageId ?? this.legacyPageId;
  }
  public async retrieveData(_liveData?: boolean) {
    let { _pageId: pageId } = this;
    if (!pageId) {
      pageId = this.legacyPageId;
    }

    return Promise.resolve({
      highlights: highlights[pageId],
      config: configs[pageId],
      cuepoints: [],
      publication: null,
    });
  }

  public getMedia() {
    const pageId = this.pageId;
    const config = configs[pageId];
    const { selectedVideo } = config;
    if (!videos[pageId]) {
      throw new Error('No videos found for this page');
    }
    if (Array.isArray(videos[pageId])) {
      return videos[pageId]?.[selectedVideo];
    } else {
      return videos[pageId];
    }
  }
  public getMediaStyle() {
    const config = configs[this.pageId];
    if (!config.styles) {
      return {};
    }
    return config?.styles?.video[config.selectedVideo] ?? config.styles.video;
  }
}

// Retrieves and stores the config for the widget
export class ApiDataRetrievalStrategy implements DataRetrievalStrategy {
  private config: WidgetConfig;

  private readonly urls: URLDictionary;
  constructor(url: string = GENIUS_API_URL) {
    this.config = this.getValuesFromWindow();

    this.urls = URLBuilder.build(url, {
      ...this.config,
      organization_id: this.config.organization_id,
    });
  }

  private getValuesFromWindow() {
    const WINDOW_CONFIGURED_VALUES: [string, boolean?][] = [
      // [1] means that the value is required
      ['app_id'],
      ['width'],
      ['product_sku'],
      ['page_id'],
      ['variation_id'],
      ['widget_base'],
      ['datasource_base'],
      ['organization_id', true],
      ['language', true],
      ['debug'],
      // Will be shown on top of the video or instead of
      // it in case we only have audio
      ['static_image_url'],
      ['video_thumbnail_url'],
    ];
    const config: Partial<WidgetConfig> = {
      widget_base: WIDGET_BASE,
      app_id: 'default',
      language: 'en-US',
      components: {
        powered_by: {
          'fr-FR': 'par',
          'en-US': 'by',
        },
      },
    };
    for (const [key, required] of WINDOW_CONFIGURED_VALUES) {
      const value = window?.[HEY_GENIUS_FIELD]?.[key];
      if (!value && required) {
        throw new Error(`window.${HEY_GENIUS_FIELD} is missing ${key}`);
      }
    }
    Object.assign(config, window?.[HEY_GENIUS_FIELD]);
    if (config.debug) console.debug('Config:', config);
    return config as WidgetConfig;
  }
  private async fetchPublication(): Promise<Publication | null> {
    try {
      const response = await fetch(encodeURI(this.urls.publication));
      if (response.status === 204) {
        return null;
      }
      if (!response.ok)
        throw new Error(
          `Received response with status code ${response.status} when fetching publication`,
        );
      return await response.json();
    } catch {
      throw new Error(
        `Could not fetch publication from ${this.urls.publication}`,
      );
    }
  }

  private async fetchAssets(): Promise<unknown[] | null> {
    const { config } = this;
    if (!config) throw new Error('No config provided!');
    if (!config.assets?.length) return null;
    const promises = config.assets?.map((asset) =>
      fetch(`${this.urls.asset}/${asset}`).then((response) => response.json()),
    );
    return promises;
  }

  private async fetchProjectConfiguration(): Promise<WidgetConfig | null> {
    // @ts-expect-error This is deprecated legacy configuration
    const legacyConfig = window?.libertify?.config;
    if (legacyConfig) {
      return Promise.resolve(this.config);
    }
    if (!this.urls.config) throw new Error('No config URL provided');
    try {
      const response = await fetch(this.urls.config);
      const customConfig = await response.json();
      // keep also defaultConfig to have to override all values in custom config
      this.config = merge.all<WidgetConfig>(
        [this.config, defaultConfig, customConfig],
        {
          arrayMerge: (_target, source) => source, // Replace instead of concat
        },
      );
    } catch (_e) {
      // Load the default config if the fetch fails
      console.warn(
        `Could not fetch config from ${this.urls.config}, using default config`,
      );
      this.config = merge(this.config, defaultConfig, {
        arrayMerge: (_target, source) => source, // Replace instead of concat
      });
    }
    return this.config;
  }

  private async fetchHighlights(highlightUrl) {
    if (!highlightUrl) throw new Error('No highlight URL provided');
    const response = await fetch(highlightUrl);
    if (!response.ok)
      throw new Error(
        `Received response with status code ${response.status} when fetching highlights`,
      );
    return await response.json();
  }

  private async fetchCuepoints(cuepointsUrl) {
    if (!cuepointsUrl) return [];
    const response = await fetch(cuepointsUrl);
    return (await response.json()).data;
  }

  private async createDataStructure(publication) {
    if (publication) {
      this.config.assets = Object.values(publication.assets).map(
        (asset) => (asset as { path: string }).path,
      );
      if (!publication.assets['Highlights_0']) {
        throw new Error('No highlights found');
      }
      const results = await this.fetchHighlights(
        publication.assets['Highlights_0'].path,
      );
      if (this.config.debug) console.debug('data', { highlights });
      if ('section_highlights' in results) {
        const converted = sourcesToHighlightsAndCuepoints(
          results.section_highlights,
          results.document_id,
        );
        return {
          config: this.config,
          highlights: converted.highlights.data,
          assets: /* assets ??  */ [],
          cuepoints: converted.cuepoints.data,
        };
      } else {
        let cuepoints = [];
        if (publication.assets['CuePoints_0']) {
          cuepoints = await this.fetchCuepoints(
            publication.assets['CuePoints_0'].path,
          );
        }
        return {
          config: this.config,
          highlights: results.data,
          cuepoints,
          assets: /* assets ??  */ [],
        };
      }
    } else if (this.config.alt_highlights_url) {
      return {
        config: this.config,
        highlights: await this.fetchHighlights(this.config.alt_highlights_url),
        assets: [],
        cuepoints: [],
      };
    } else {
      throw new Error(
        'No publication found and no alternative highlights url provided',
      );
    }
  }

  public async retrieveData(liveData?: any) {
    await this.fetchProjectConfiguration();
    if (!this.config) throw new Error('Error retrieving config');
    const pageName = this.config?.page_id;
    let fetchedHighlights = [];
    if (this.config.alt_highlights_url) {
      const highlightsUrl = this.config.alt_highlights_url?.endsWith?.('.json')
        ? this.config.alt_highlights_url
        : `${this.config.widget_base}/data/organizations/${this.config?.organization_id}/pages/${pageName}/highlights.json`;
      fetchedHighlights = await fetch(highlightsUrl).then((res) => res.json());
    }
    let fetchedLegacyConfig = {};

    if (this.config.alt_config_url) {
      fetchedLegacyConfig = await fetch(this.config.alt_config_url).then(
        (res) => res.json(),
      );
      this.config = { ...this.config, ...fetchedLegacyConfig };
      return {
        config: { ...this.config, ...fetchedLegacyConfig },
        highlights: fetchedHighlights ?? [],
        assets: [],
        cuepoints: [],
        publication: null,
      };
    }
    let publication: Publication | null = null;
    if (!liveData) {
      try {
        publication = await this.fetchPublication();
        if (publication === null) {
          throw new Error('Publication not found!');
        }
      } catch (e) {
        if (this.config.alt_highlights_url) {
          console.warn(
            'Could not fetch publication, but alternative highlights url present; continuing',
          );
        } else {
          throw e;
        }
      }
    }
    if (!this.config.media_type) {
      throw new Error('No media type provided in the config');
    }
    this.config.media =
      this.config.media_url ?? publication?.media?.url ?? liveData?.media?.url;

    const dataStructure = await this.createDataStructure(
      publication ?? liveData,
    );
    return { ...dataStructure, publication };
  }

  public getMedia() {
    if (!this.config?.media) throw new Error('No config for media provided');
    if (Array.isArray(this.config.media)) {
      return this.config.media[this.config.variation_id ?? 0];
    }
    return this.config.media;
  }
  public getMediaStyle() {
    if (!this.config?.styles?.media)
      throw new Error('No config for styles provided');
    if (this.config.static_image_url) {
      return this.config.styles?.image?.[this.config.variation_id ?? 0];
    }
    return this.config.styles.media[this.config.variation_id ?? 0];
  }
}
